import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { ErrorBanner, Spinner } from './common'
import { DataTable } from './DataTable'

/**
 * La lista dietro un numero di Traffic Analysis (`/flows/analysis/devices`).
 *
 * Un numero che non si può aprire lascia la domanda «quali sono?» senza
 * risposta: questa finestra la dà, e da ogni riga si va dove serve — il CI
 * abbinato, lo switch sulla porta (che apre la finestra della porta), il
 * catalogo. Stessa forma della finestra della porta (`.dob-modal`), così chi
 * ha imparato una ha imparato l'altra.
 */
export function ElencoDispositivi({
  titolo,
  filter,
  value,
  onChiudi,
}: {
  titolo: string
  filter: string
  value?: string
  onChiudi: () => void
}) {
  const dati = useAsync(() => api.trafficAnalysisDevices(filter, value), [filter, value])
  const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString(localeCorrente()) : '—')

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={titolo}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{titolo}</h3>
            <p className="dob-modal__sotto">
              {dati.data
                ? dati.data.truncated
                  ? t('{n} dispositivi (i primi {n}: restringi con il filtro).', { n: dati.data.count })
                  : t('{n} dispositivi', { n: dati.data.count })
                : t('Caricamento…')}
            </p>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        <ErrorBanner error={dati.error} />
        {dati.loading && <Spinner />}
        {dati.data && (
          <div className="dob-modal__corpo">
            <DataTable
              rows={dati.data.rows}
              rowKey={(r) => r.mac}
              conteggio={{ nome: t('dispositivi') }}
              emptyText={t('Nessun dispositivo per questo filtro.')}
              columns={[
                {
                  key: 'mac', header: 'MAC', mono: true, value: (r) => (r.report ? `${r.mac} ${t('segnalato')}` : r.mac),
                  render: (r) => (
                    <>
                      {r.mac}
                      {r.report && (
                        <>
                          {' '}<span className="badge" title={r.report.note || t('Segnalazione aperta')}>{t('segnalato')}</span>
                        </>
                      )}
                    </>
                  ),
                },
                {
                  key: 'ip', header: 'IP', mono: true, value: (r) => r.ip,
                  render: (r) => (
                    <>
                      {r.ip ?? '—'}
                      {r.vlan != null && <small className="text-muted"> VLAN {r.vlan}</small>}
                    </>
                  ),
                },
                { key: 'hostname', header: t('Nome') },
                {
                  key: 'vendor', header: t('Costruttore'), value: (r) => [r.vendor, r.catalogue_source ? t('catalogo') : null].filter(Boolean).join(' '),
                  render: (r) => (
                    <>
                      {r.vendor ?? <span className="text-muted">—</span>}
                      {r.catalogue_source && (
                        <>
                          {' '}<span className="badge" title={t('Voce del catalogo CENSO')}>{t('catalogo')}</span>
                        </>
                      )}
                    </>
                  ),
                },
                {
                  key: 'kind', header: t('Tipo'),
                  value: (r) => [r.kind, r.model, r.series, r.category, r.product?.vendor, r.os, r.medical ? t('elettromedicale') : null].filter(Boolean).join(' '),
                  render: (r) => (
                    <>
                      {[r.kind, r.model || (r.series ? t('serie {s}', { s: r.series }) : null), r.category].filter(Boolean).join(' · ') || <span className="text-muted">—</span>}
                      {r.product && <> <small className="text-muted" title={t('riconosciuto da «{e}»', { e: r.product.evidence })}>({r.product.vendor})</small></>}
                      {r.os && <> <small className="text-muted">{r.os}</small></>}
                      {r.medical && (
                        <>
                          {' '}<span className="badge badge--warn">{t('elettromedicale')}</span>
                        </>
                      )}
                    </>
                  ),
                },
                {
                  key: 'ci', header: t('Oggetto'), value: (r) => r.ci_name ?? t('non in CMDB'),
                  render: (r) => (
                    r.ci_id ? (
                      <Link to={`/cis/${r.ci_id}`} title={r.ci_class ?? undefined}>{r.ci_name}</Link>
                    ) : (
                      <span className="text-muted">{t('non in CMDB')}</span>
                    )
                  ),
                },
                {
                  key: 'ports', header: t('Switch · porta'),
                  value: (r) => r.ports.map((p) => `${p.switch_name} ${p.if_name}`).join(', '),
                  render: (r) => (
                    <>
                      {r.ports.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        r.ports.slice(0, 3).map((p, i) => (
                          <span key={`${p.switch_id}-${p.if_name}`}>
                            {i > 0 && ', '}
                            <Link to={`/cis/${p.switch_id}#porta-${encodeURIComponent(p.if_name)}`} title={t('Apri la finestra della porta')}>
                              {p.switch_name} · {p.if_name}
                            </Link>
                            {p.vlan != null && <small className="text-muted"> VLAN {p.vlan}</small>}
                          </span>
                        ))
                      )}
                      {r.ports.length > 3 && <small className="text-muted"> +{r.ports.length - 3}</small>}
                    </>
                  ),
                },
                {
                  key: 'last_seen', header: t('Ultima vista'), type: 'date',
                  render: (r) => <small>{quando(r.last_seen)}</small>,
                },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  )
}
